import { Button, Form, Spinner } from 'react-bootstrap';
import {
  useContext,
  useDebugValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import QRCode from 'react-qr-code';
import styles from './style.module.scss';
import { WalletIcon, CopyToClipboardIcon } from '../../assets/Icon';
import Input from '../../components/ui/Input';
import ProfileUpload from '../../components/ui/ProfileUpload';
import AuthContext from '../../contexts/AuthContext';
import WalletContext from '../../contexts/WalletContext';
import useAPI from '../../hooks/useAPI';
import useDebounce from '../../hooks/useDebounce';

export default function ProfilePage() {
  const { userData: user, updateUserProfile } = useContext(AuthContext);
  const renderFlag = useRef(false);
  const [userName, setUserName] = useState(
    `${user?.firstName?.trim?.() || ''} ${user?.lastName?.trim?.() || ''}`.trim(),
  );

  const { isLoading, refetch: refetchUserData } = useAPI({
    url: '/auth/get/info',
    method: 'GET',
    onSuccess: data => {
      // console.log('🚀 ~ AuthProvider ~ data:', data);
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'An error occurred');
    },
  });

  const debouncedValue = useDebounce(userName, 500);
  const { refetch: updateProfile, isLoading: isProfileUpdating } = useAPI({
    url: '/auth/user/profile/update',
    method: 'PUT',
    onError: error => {
      toast.error(error?.response?.data?.message || 'An error occurred');
    },
    onSuccess: data => {
      const newUserData = data.result.data;
      updateUserProfile({
        image: newUserData.image,
        firstName: newUserData.firstName,
        lastName: newUserData.lastName,
      });
      toast.success('Profile updated successfully.');
    },
  });

  const copyWalletAddress = () => {
    navigator.clipboard.writeText(userWalletAddress || '');
    toast.success('Copied to clipboard');
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const sanitizedValue = value.replace(/[^a-zA-Z ]/g, '');
    setUserName(sanitizedValue);
  };

  const userEmail = useMemo(() => {
    return user?.email;
  }, [user]);

  const userWalletAddress = useMemo(() => {
    return user?.wallet?.[0]?.walletAddress;
  }, [user]);

  useEffect(() => {
    if (renderFlag.current) {
      const [firstName, lastName] = debouncedValue.split(' ');
      if (!firstName && !lastName) {
        toast.error('Name should not be empty.');
      } else {
        updateProfile({ firstName: firstName || '', lastName: lastName || '' });
      }
    }

    renderFlag.current = true;
  }, [debouncedValue]);
  return (
    <div className={styles.container}>
      <div className="d-flex align-items-center">
        <h2 className="mb-0 fw-bold">Profile Details</h2>
        {isProfileUpdating && (
          <span className="d-flex align-items-center gap-2 badge rounded-pill bg-light text-secondary">
            <Spinner
              animation="border"
              role="status"
              variant="primary"
              size={'sm'}
            />
            Updating...
          </span>
        )}
      </div>
      <div className="my-3">
        <ProfileUpload
          onChange={fileURL => updateProfile({ image: fileURL || '' })}
        />
      </div>
      <Form.Group className="mb-3">
        <Form.Label htmlFor="" className="fw-bold">
          Profile Name
        </Form.Label>
        <Input
          label=""
          type="text"
          id="otp"
          placeholder="Enter your full name"
          value={userName}
          onChange={handleNameChange}
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label htmlFor="" className="fw-bold">
          Email Id
        </Form.Label>
        <Input label="" type="text" id="otp" value={userEmail} disabled />
      </Form.Group>

      <div
        className={
          'd-flex align-items-center gap-3 my-5 py-4 border-top border-bottom border-1 ' +
          styles.divider
        }
      >
        <div>
          <WalletIcon size={75} color="var(--primary)" />
        </div>
        <div>
          <h4 className="mb-0 fw-bold">Wallet Address</h4>
          <div className="d-flex align-items-center">
            <p className="mb-0 fw-bold">{userWalletAddress}</p>
            <Button variant="plain" onClick={copyWalletAddress}>
              <CopyToClipboardIcon color="var(--primary)" />
            </Button>
          </div>
        </div>
        <div>
          <QRCode
            size={120}
            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
            className={styles.qrCode}
            value={userWalletAddress || ''}
            viewBox={`0 0 256 256`}
          />
        </div>
      </div>
    </div>
  );
}
